import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { KsgPopover, KsgSelect, KsgSwitch, KsgButton, KsgIcon, KsgTooltip } from '@ksg/ui';
import { TimeCategory, TimeTrackingService } from '../../../core/time-tracking/time-tracking.service';
import { AccountantCompaniesService } from '../../../core/company/accountant-companies.service';
import { AuthService } from '../../../core/auth/auth.service';

const CATEGORIES: TimeCategory[] = ['bookkeeping', 'vat', 'payroll', 'consultation', 'other'];

@Component({
  selector: 'app-time-tracker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoPipe, KsgPopover, KsgSelect, KsgSwitch, KsgButton, KsgIcon, KsgTooltip],
  template: `
    @if (auth.isAuthenticated() && companies.selectedCompanyId()) {
      <div class="time-tracker-wrap">
        <button
          type="button"
          class="time-tracker-chip"
          [class.time-tracker-chip-running]="tracker.running()"
          [disabled]="busy()"
          (click)="toggle()"
          [ksgTooltip]="tooltipKey() | transloco: { name: activeCompanyName() }"
          ksgTooltipPosition="bottom"
          [attr.aria-label]="tooltipKey() | transloco: { name: activeCompanyName() }"
        >
          <ksg-icon [name]="tracker.running() ? 'pi-stop-circle' : 'pi-play-circle'" [size]="18" />
          @if (tracker.running()) {
            <span class="time-tracker-value">{{ tracker.displayTime() }}</span>
            <span class="time-tracker-company">{{ activeCompanyName() }}</span>
          } @else {
            <span class="time-tracker-label">{{ 'timeTracking.start' | transloco }}</span>
          }
        </button>

        @if (!tracker.running()) {
          <button
            #optTrigger
            type="button"
            class="time-tracker-options"
            [disabled]="busy()"
            (click)="optOpen.set(!optOpen())"
            [ksgTooltip]="'timeTracking.optionsTooltip' | transloco"
            ksgTooltipPosition="bottom"
            [attr.aria-label]="'timeTracking.optionsTooltip' | transloco"
          >
            <ksg-icon name="pi-sliders-h" [size]="18" />
          </button>

          <ksg-popover [anchor]="optTrigger" [(open)]="optOpen" position="bottom" [ariaLabel]="'timeTracking.optionsTitle' | transloco">
            <div class="flex flex-col gap-3" style="width: 16rem">
              <span class="font-semibold">{{ 'timeTracking.optionsTitle' | transloco }}</span>
              <ksg-select
                [label]="'timeTracking.reports.col.category' | transloco"
                [options]="categoryOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="category()"
                (ngModelChange)="category.set($event)"
              />
              <ksg-switch
                [label]="'timeTracking.reports.col.billable' | transloco"
                [ngModel]="billable()"
                (ngModelChange)="billable.set($event)"
              />
              <ksg-button
                icon="pi-play"
                [disabled]="busy()"
                (click)="startWithOptions()"
              >{{ 'timeTracking.start' | transloco }}</ksg-button>
            </div>
          </ksg-popover>
        }
      </div>
    }
  `,
  styles: [
    `
      .time-tracker-wrap {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }

      .time-tracker-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        height: 2.5rem;
        padding: 0 0.875rem;
        border-radius: 1.25rem;
        border: var(--border-w) solid var(--border);
        background: transparent;
        color: var(--text);
        font-variant-numeric: tabular-nums;
        font-size: 0.95rem;
        line-height: 1;
        cursor: pointer;
        transition:
          background-color 0.15s,
          border-color 0.15s,
          color 0.15s;
      }

      .time-tracker-value {
        min-width: 4.5rem;
        text-align: center;
      }

      .time-tracker-company {
        max-width: 9rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        opacity: 0.75;
        font-size: 0.85rem;
      }

      .time-tracker-chip:hover {
        background: var(--surface-2);
      }

      .time-tracker-chip:focus-visible,
      .time-tracker-options:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px var(--focus);
      }

      .time-tracker-chip:disabled,
      .time-tracker-options:disabled {
        opacity: 0.6;
        cursor: default;
      }

      .time-tracker-chip-running {
        background: var(--success);
        border-color: var(--success);
        color: var(--on-primary, #fff);
      }

      .time-tracker-chip-running:hover {
        background: var(--success);
        border-color: var(--success);
      }

      .time-tracker-options {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.5rem;
        border-radius: 0.75rem;
        border: var(--border-w) solid var(--border);
        background: transparent;
        color: var(--text);
        cursor: pointer;
      }

      .time-tracker-options:hover {
        background: var(--surface-2);
      }
    `,
  ],
})
export class TimeTracker {
  readonly tracker = inject(TimeTrackingService);
  readonly companies = inject(AccountantCompaniesService);
  readonly auth = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  readonly busy = signal(false);
  readonly category = signal<TimeCategory>('bookkeeping');
  readonly billable = signal(true);
  readonly optOpen = signal(false);

  readonly categoryOptions = computed(() =>
    CATEGORIES.map((value) => ({
      label: this.transloco.translate('timeTracking.category.' + value),
      value,
    })),
  );

  readonly activeCompanyName = computed(
    () => this.tracker.runningEntry()?.companyName ?? this.companies.selectedCompany()?.name ?? '',
  );

  readonly tooltipKey = computed(() =>
    this.tracker.running() ? 'timeTracking.runningTooltip' : 'timeTracking.startTooltip',
  );

  toggle(): void {
    if (this.busy()) return;
    this.busy.set(true);
    const request$ = this.tracker.running()
      ? this.tracker.stop()
      : this.tracker.start({ category: this.category(), billable: this.billable() });
    request$.subscribe({
      next: () => this.busy.set(false),
      error: () => this.busy.set(false),
    });
  }

  startWithOptions(): void {
    this.optOpen.set(false);
    if (this.busy() || this.tracker.running()) return;
    this.busy.set(true);
    this.tracker.start({ category: this.category(), billable: this.billable() }).subscribe({
      next: () => this.busy.set(false),
      error: () => this.busy.set(false),
    });
  }
}
